import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { posApi } from '@/api/pos.api';
import type { PaymentRequest } from '@/types/billing';

const stripeCache = new Map<string, Promise<Stripe | null>>();

function getStripe(publishableKey: string) {
  if (!stripeCache.has(publishableKey)) {
    stripeCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeCache.get(publishableKey)!;
}

export async function collectStripeOnlinePayment(params: {
  storeId: number;
  invoiceId: number;
  amount: number;
  currency: string;
  session: Record<string, unknown>;
}): Promise<PaymentRequest> {
  const publishableKey = String(params.session.publishableKey ?? '');
  const clientSecret = String(params.session.clientSecret ?? '');
  if (!publishableKey || !clientSecret) {
    throw new Error('Stripe session missing publishableKey or clientSecret — configure Stripe gateway keys in POS setup');
  }
  const stripe = await getStripe(publishableKey);
  if (!stripe) throw new Error('Stripe.js failed to load');

  const result = await stripe.confirmPayment({
    clientSecret,
    confirmParams: { return_url: window.location.href },
    redirect: 'if_required',
  });
  if (result.error) {
    throw new Error(result.error.message ?? 'Stripe payment failed');
  }
  const intent = result.paymentIntent;
  return {
    paymentMethod: 'STRIPE',
    amount: params.amount,
    gatewayProvider: 'STRIPE',
    gatewayTransactionId: intent?.id ?? clientSecret,
    gatewayStatus: (intent?.status ?? 'succeeded').toUpperCase(),
    gatewayReference: String(params.session.paymentIntentId ?? intent?.id ?? ''),
    transactionRef: intent?.id,
  };
}

/** Stripe Terminal card-present flow. Uses simulated readers only when testMode is true. */
export async function collectStripeTerminalPayment(params: {
  storeId: number;
  invoiceId: number;
  amount: number;
  currency: string;
  testMode?: boolean;
}): Promise<PaymentRequest> {
  const [{ secret: connectionToken }, intentSession] = await Promise.all([
    posApi.stripeConnectionToken(params.storeId),
    posApi.stripeTerminalPaymentIntent({
      storeId: params.storeId,
      amount: params.amount,
      currency: params.currency,
      invoiceId: params.invoiceId,
    }),
  ]);

  const { loadStripeTerminal } = await import('@stripe/terminal-js');
  const StripeTerminal = await loadStripeTerminal();
  if (!StripeTerminal) throw new Error('Stripe Terminal SDK failed to load');
  const terminal = StripeTerminal.create({
    onFetchConnectionToken: async () => connectionToken,
  });

  const simulated = params.testMode ?? import.meta.env.DEV;
  const discover = await terminal.discoverReaders({ simulated });
  if ('error' in discover) {
    throw new Error(discover.error.message ?? 'Reader discovery failed');
  }
  const readers = discover.discoveredReaders ?? [];
  if (!readers.length) throw new Error('No Stripe Terminal readers found');

  const connect = await terminal.connectReader(readers[0]);
  if ('error' in connect) {
    throw new Error(connect.error.message ?? 'Reader connect failed');
  }

  const clientSecret = String(intentSession.clientSecret ?? '');
  const collect = await terminal.collectPaymentMethod(clientSecret);
  if ('error' in collect) {
    throw new Error(collect.error.message ?? 'Collect payment method failed');
  }

  const processed = await terminal.processPayment(collect.paymentIntent);
  if ('error' in processed) {
    throw new Error(processed.error.message ?? 'Terminal process payment failed');
  }

  const pi = processed.paymentIntent;
  return {
    paymentMethod: 'STRIPE',
    amount: params.amount,
    gatewayProvider: 'STRIPE_TERMINAL',
    gatewayTransactionId: pi.id,
    gatewayStatus: (pi.status ?? 'succeeded').toUpperCase(),
    gatewayReference: String(intentSession.paymentIntentId ?? pi.id),
    transactionRef: pi.id,
  };
}
